import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Bell, Gift, Users, Send, RefreshCw, CalendarX } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const COUNTRY_CODES = [
  { code: "+91", flag: "🇮🇳" },
  { code: "+1", flag: "🇺🇸" },
  { code: "+44", flag: "🇬🇧" },
  { code: "+971", flag: "🇦🇪" },
];

interface RecurringReminderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requestId?: string;
  recipientName?: string;
  productName?: string;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function RecurringReminderDialog({
  open,
  onOpenChange,
  requestId,
  recipientName: initialRecipientName,
  productName,
}: RecurringReminderDialogProps) {
  const [step, setStep] = useState<"ask" | "form">("ask");
  const [date, setDate] = useState<Date | undefined>();
  const [eventName, setEventName] = useState(productName ? `${productName} Gift` : "");
  const [recipientName, setRecipientName] = useState(initialRecipientName || "");
  const [message, setMessage] = useState("");
  const [reminderDays, setReminderDays] = useState("7");
  const [occasion, setOccasion] = useState("birthday");
  const [isRecurring, setIsRecurring] = useState(true);
  const [inviteFriend, setInviteFriend] = useState({ email: "", phone: "", phoneCountryCode: "+91" });
  const [saving, setSaving] = useState(false);

  const OCCASIONS = [
    { value: "birthday", label: "🎂 Birthday" },
    { value: "anniversary", label: "💍 Anniversary" },
    { value: "wedding", label: "👰 Wedding" },
    { value: "baby_shower", label: "👶 Baby Shower" },
    { value: "graduation", label: "🎓 Graduation" },
    { value: "housewarming", label: "🏠 Housewarming" },
    { value: "retirement", label: "🎉 Retirement" },
    { value: "promotion", label: "🚀 Promotion" },
    { value: "festival", label: "🪔 Festival" },
    { value: "other", label: "✨ Other" },
  ];

  const handleYes = () => {
    setStep("form");
  };

  const handleNo = () => {
    onOpenChange(false);
    setStep("ask");
  };

  const handleSave = async () => {
    if (!date) {
      toast.error("Please select a date");
      return;
    }
    if (!eventName.trim()) {
      toast.error("Please enter an event name");
      return;
    }

    setSaving(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("Please log in to save reminders");
        setSaving(false);
        return;
      }

      const { error } = await supabase
        .from("gift_reminders")
        .insert({
          user_id: user.id,
          event_name: eventName.trim(),
          recipient_name: recipientName.trim() || null,
          event_date: format(date, "yyyy-MM-dd"),
          message: message.trim() || null,
          reminder_days_before: parseInt(reminderDays),
          is_recurring: isRecurring,
          created_from_request_id: requestId || null,
          occasion: occasion || null,
        });

      if (error) {
        console.error("Error saving reminder:", error);
        toast.error("Failed to save reminder");
      } else {
        toast.success("Reminder saved! We'll notify you before the next event.");
        onOpenChange(false);
        setStep("ask");
        // Reset form
        setDate(undefined);
        setEventName("");
        setRecipientName("");
        setMessage("");
        setReminderDays("7");
        setOccasion("birthday");
        setIsRecurring(true);
        setInviteFriend({ email: "", phone: "", phoneCountryCode: "+91" });
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => {
      if (!open) {
        setStep("ask");
      }
      onOpenChange(open);
    }}>
      <DialogContent className="sm:max-w-md">
        {step === "ask" ? (
          <>
            <DialogHeader>
              <div className="mx-auto mb-4 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Bell className="h-6 w-6 text-primary" />
              </div>
              <DialogTitle className="text-center">Set a Reminder for Next Year?</DialogTitle>
              <DialogDescription className="text-center">
                Is this a recurring event like a birthday or anniversary? We can remind you to organize the group gift again next year!
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col gap-3 mt-4">
              <div className="flex flex-col sm:flex-row gap-2 w-full">
                <Button variant="outline" onClick={handleNo} className="flex-1">
                  No, thanks
                </Button>
                <Button onClick={handleYes} className="flex-1">
                  <Gift className="h-4 w-4 mr-2" />
                  Yes, remind me!
                </Button>
              </div>
              
              {/* Invite Friends Option */}
              <div className="pt-3 border-t w-full">
                <p className="text-xs text-muted-foreground text-center mb-3">
                  Know someone who'd love group gifting?
                </p>
                <div className="flex gap-2 justify-center">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700"
                    onClick={() => {
                      const url = "https://splitkart.app";
                      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                    }}
                  >
                    <Users className="h-3.5 w-3.5 mr-1.5" />
                    Facebook
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Set a Reminder</DialogTitle>
              <DialogDescription>
                We'll remind you before the event so you can organize a group purchase!
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {/* Occasion Selector */}
              <div className="space-y-2">
                <Label>Occasion</Label>
                <Select value={occasion} onValueChange={setOccasion}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select occasion type" />
                  </SelectTrigger>
                  <SelectContent>
                    {OCCASIONS.map((occ) => (
                      <SelectItem key={occ.value} value={occ.value}>
                        {occ.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">What type of event is this?</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="eventName">Event Name *</Label>
                <Input
                  id="eventName"
                  placeholder="e.g., John's 30th Birthday, Wedding Anniversary"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Give this event a memorable name</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="recipientName">Recipient Name *</Label>
                <Input
                  id="recipientName"
                  placeholder="e.g., John, Mom & Dad"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Who is this event for?</p>
              </div>

              <div className="space-y-2">
                <Label>Event Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Recurring Toggle */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2">
                  {isRecurring ? (
                    <RefreshCw className="h-4 w-4 text-customer" />
                  ) : (
                    <CalendarX className="h-4 w-4 text-muted-foreground" />
                  )}
                  <div>
                    <p className="text-sm font-medium">{isRecurring ? "Recurring Event" : "One-time Event"}</p>
                    <p className="text-xs text-muted-foreground">
                      {isRecurring ? "Reminds you every year" : "Only reminds once"}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={isRecurring}
                  onCheckedChange={setIsRecurring}
                />
              </div>

              <div className="space-y-2">
                <Label>Remind Me</Label>
                <Select value={reminderDays} onValueChange={setReminderDays}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select timing" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 days before</SelectItem>
                    <SelectItem value="7">7 days before</SelectItem>
                    <SelectItem value="14">14 days before</SelectItem>
                    <SelectItem value="30">30 days before</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Invite a Friend */}
              <div className="space-y-3 p-3 bg-customer/5 rounded-lg border border-customer/10">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-customer" />
                  <Label className="text-sm font-medium">Invite a Friend to This Event</Label>
                </div>
                <p className="text-xs text-muted-foreground">Want to organize a group purchase together? Invite a friend!</p>
                <div className="space-y-2">
                  <Input
                    type="email"
                    placeholder="Friend's email"
                    value={inviteFriend.email}
                    onChange={(e) => setInviteFriend(prev => ({ ...prev, email: e.target.value }))}
                    className="h-9"
                  />
                  <div className="flex gap-2">
                    <Select 
                      value={inviteFriend.phoneCountryCode} 
                      onValueChange={(v) => setInviteFriend(prev => ({ ...prev, phoneCountryCode: v }))}
                    >
                      <SelectTrigger className="w-20 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRY_CODES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.flag} {c.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="tel"
                      placeholder="Friend's phone (optional)"
                      value={inviteFriend.phone}
                      onChange={(e) => setInviteFriend(prev => ({ ...prev, phone: e.target.value.replace(/\D/g, '') }))}
                      className="flex-1 h-9"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Personal Note (optional)</Label>
                <Textarea
                  id="message"
                  placeholder="Gift ideas, budget suggestions, preferences..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setStep("ask")}>
                Back
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-customer hover:bg-customer/90 text-customer-foreground">
                {saving ? "Saving..." : "Add Reminder"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
